import {
  Anchor, Badge, Button, Container, Group, Pagination, Stack, Switch,
  Table, Text, Title,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { AgreementPicker } from '../components/EntityPicker';
// Task #239 (2026-06-02): PersonRef removed — Person column now reads
// server-resolved person_display_name on the AgreementPartyOut payload.
// AgreementRef retained for the Agreement column.
// Task #362 (2026-06-09 §3f sweep): trashcan + restore removed from list rows.
// Delete/restore now live exclusively on the agreement-party detail page Edit mode,
// mirroring the app-wide convention established by Task #245.
import { AgreementRef } from '../components/EntityRef';
import { useAgreementParties } from '../hooks/useAgreementParties';
import { PARTY_ROLE_LABELS } from '../api/types';
import {
  SortableTh, nextSort, sortParam, type SortState,
} from '../components/SortableHeader';
import { StatusExplained } from '../components/StatusExplained';

const PAGE_SIZE = 50;
type AgreementPartySortKey = 'person' | 'agreement' | 'role' | 'ubo' | 'status';
const ROLE_COLOURS: Record<string, string> = {
  HOLDER: 'blue', REPRESENTATIVE: 'grape', BENEFICIARY: 'orange',
};

export function AgreementPartiesListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const agreementId = searchParams.get('agreement_id') ?? '';
  // P41 (2026-10-15): header sort, server-side across pages; null = oldest first.
  const [sort, setSortState] = useState<SortState<AgreementPartySortKey> | null>(null);
  function onSort(k: AgreementPartySortKey) {
    setSortState((s) => nextSort(s, k));
    setPage(1);
  }

  const { data, isLoading, error } = useAgreementParties({
    agreement_id: agreementId || undefined,
    include_deleted: includeDeleted,
    sort: sortParam(sort),
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  // Task #362 (§3f sweep): del / restore / confirmDelete removed — list rows
  // no longer trigger destructive actions. See detail page top-right.
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Container size="xl">
      <Stack>
        <Group justify="space-between">
          <Title order={2}>Agreement parties</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/agreement-parties/new')}>
            New role assignment
          </Button>
        </Group>

        <Group align="flex-end">
          <div style={{ flex: 1 }}>
            <AgreementPicker
              label="Filter by agreement"
              value={agreementId || null}
              onChange={(v) => {
                setSearchParams(v ? { agreement_id: v } : {});
                setPage(1);
              }}
            />
          </div>
          <Switch label="Include deleted" checked={includeDeleted}
            onChange={(e) => { setIncludeDeleted(e.currentTarget.checked); setPage(1); }} />
        </Group>

        {error && <Text c="red">{error.detail}</Text>}

        <Table highlightOnHover withTableBorder layout="fixed">
          <Table.Thead>
            <Table.Tr>
              {/* P41 (2026-10-15): every header sorts (server-side, across pages). */}
              <SortableTh label="Person" col="person" sort={sort} onSort={onSort} w="20%" />
              <SortableTh label="Agreement" col="agreement" sort={sort} onSort={onSort} w="20%" />
              <SortableTh label="Role" col="role" sort={sort} onSort={onSort} w="20%" />
              <SortableTh label="UBO" col="ubo" sort={sort} onSort={onSort} w="20%" />
              <SortableTh label="Status" col="status" sort={sort} onSort={onSort} w="20%" />
              {/* Task #362 (2026-06-09 §3f sweep): Actions column removed —
                  delete/restore live exclusively on the detail page. */}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && <Table.Tr><Table.Td colSpan={5}><Text c="dimmed">Loading...</Text></Table.Td></Table.Tr>}
            {data?.items.length === 0 && (
              <Table.Tr><Table.Td colSpan={5}><Text c="dimmed">No role assignments found.</Text></Table.Td></Table.Tr>
            )}
            {data?.items.map((p) => (
              <Table.Tr key={p.id} style={{ cursor: 'pointer', opacity: p.deleted_at ? 0.5 : 1 }}
                onClick={() => navigate(`/agreement-parties/${p.id}`)}>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  {p.person_display_name ? (
                    <Anchor component={Link} to={`/persons/${p.person_id}`} size="sm">
                      {p.person_display_name}
                    </Anchor>
                  ) : (
                    <Text size="xs" ff="monospace" c="dimmed">{p.person_id.slice(0, 8)}…</Text>
                  )}
                </Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <AgreementRef id={p.agreement_id} linkPathPrefix="/agreements" />
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" color={ROLE_COLOURS[p.role]}>{PARTY_ROLE_LABELS[p.role] ?? p.role}</Badge>
                </Table.Td>
                <Table.Td>{p.is_ubo ? <Badge color="red" variant="light">UBO</Badge> : '-'}</Table.Td>
                <Table.Td>
                  <StatusExplained entity="party" status={p.deleted_at ? 'Deleted' : 'Active'}>
                  {p.deleted_at
                    ? <Badge color="gray" variant="light">Deleted</Badge>
                    : <Badge color="green" variant="light">Active</Badge>}
                  </StatusExplained>
                </Table.Td>
                {/* Task #362 (§3f sweep): per-row trashcan + restore removed.
                    Delete/Undelete live on the agreement-party detail page. */}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {data && data.total > PAGE_SIZE && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">{data.total} total</Text>
            <Pagination total={totalPages} value={page} onChange={setPage} />
          </Group>
        )}
      </Stack>
    </Container>
  );
}
